import React, {memo, useState} from 'react';
import {Tab} from 'semantic-ui-react';

import {type IssueLine} from '../../state/issues/types';
import {
  PanelChartModal,
  PanelChartPreview,
  PanelChartSettings,
} from '../PanelCommon';
import {EditDataTab} from './EditPanelTabs/EditDataTab';
import {EditDisplayPreferencesTab} from './EditPanelTabs/EditDisplayPreferencesTab';
import {EditExpressionsTab} from './EditPanelTabs/EditExpressionsTab';
import {EditGroupingTab} from './EditPanelTabs/EditGroupingTab';
import {GraphContainer} from './GraphContainer';
import {type RunsLinePlotPanelInnerProps} from './types';

export const ConfigWrapperComp = (
  props: RunsLinePlotPanelInnerProps & {
    issueLines?: IssueLine[];
  }
) => {
  const {data, keyInfo} = props;

  if (data.histories == null || keyInfo == null) {
    return <p>No Histories</p>;
  }

  return <Config {...props} keyInfo={keyInfo} />;
};

export const ConfigWrapper = memo(ConfigWrapperComp);

export type ConfigProps = Omit<RunsLinePlotPanelInnerProps, 'keyInfo'> & {
  /**
   * The `featureSupport` prop is used to conditionally show/hide certain tabs based on active support by plot mode. Sampled charts support all tabs, while bucketed charts will move toward feature parity over time. The `featureSupport` optional property allows incremental rollout of features in bucketing.
   */
  featureSupport?: {
    expressions: boolean;
    grouping: boolean;
  };
  issueLines?: IssueLine[];
  keyInfo: NonNullable<RunsLinePlotPanelInnerProps['keyInfo']>;
};

export const Config = memo(function PanelRunsLinePlotConfig(
  props: ConfigProps
) {
  const {config, updateConfig} = props;

  const dataTab = (
    <EditDataTab
      updateConfig={updateConfig}
      config={config}
      query={props.query}
      lines={props.lines}
    />
  );

  const groupingTab = (
    <EditGroupingTab
      config={config}
      updateConfig={updateConfig}
      pageQuery={props.pageQuery}
      data={props.data}
    />
  );

  const displayPreferencesTab = (
    <EditDisplayPreferencesTab
      config={config}
      updateConfig={updateConfig}
      pageQuery={props.pageQuery}
      lines={props.lines}
    />
  );

  const expressionsTab = (
    <EditExpressionsTab updateConfig={updateConfig} config={config} />
  );

  const [tabActiveIndex, setTabActiveIndex] = useState(0);

  const settingsPanes = [
    {
      isShown: true,
      menuItem: 'Data',
      render: () => dataTab,
    },
    {
      isShown: true,
      menuItem: 'Grouping',
      render: () => groupingTab,
    },
    {
      isShown: true,
      menuItem: 'Display preferences',
      render: () => displayPreferencesTab,
    },
    {
      isShown: true,
      menuItem: 'Expressions',
      render: () => expressionsTab,
    },
  ].filter(pane => pane.isShown);

  return (
    <PanelChartModal id="panel-runs-line-plot-modal">
      <PanelChartPreview>
        <GraphContainer {...props} />
      </PanelChartPreview>
      <PanelChartSettings>
        <Tab
          data-dd-action-name={`Line plot settings tab: ${settingsPanes[tabActiveIndex].menuItem}`}
          activeIndex={tabActiveIndex}
          onTabChange={(e, {activeIndex}) => {
            if (typeof activeIndex === 'number') {
              setTabActiveIndex(activeIndex);
            }
          }}
          panes={settingsPanes}
          menu={{
            secondary: true,
            pointing: true,
            className: 'chart-settings-tab-menu',
          }}
        />
      </PanelChartSettings>
    </PanelChartModal>
  );
});
